import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import CustomTextField from "../../common/CustomTextField";
import {
  AttachmentBarcodeStickerInfo,
  OrderItemInfo,
  OrderInfo,
  TissueTrackingInfo,
  addTissueTracking,
  getOrderItemsByOrderId,
  updateElementInOrderSlice,
  useOrderSlice,
  AttachmentImageOfWoundsInfo,
  AttachmentMedicalNotesInfo,
  fetchOrderDocumentById,
  fetchOrderDocumentsByIds,
} from "../../../redux/slice/order/OrderSlice";
import { useForm } from "react-hook-form";
import {
  AssignmentOutlined,
  Cancel,
  ClassOutlined,
  NoteAddOutlined,
  PersonOutlined,
  WallpaperOutlined,
} from "@mui/icons-material";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { useLocation } from "react-router-dom";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomDatePicker from "../../common/CustomDatePicker";
import { useEffect, useMemo, useState } from "react";
import { convertBase64 } from "../../../utils/Utility";
import dayjs from "dayjs";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import React from "react";
import CustomHelpMessage from "../../common/CustomHelpMessage";
import { ORDER_AVAILABILITY, RESPONSE_CODES, USER_ROLE_ENTITY_NAMES, USER_ROLES } from "../../../constants/applicationConstants";
import { useNavigation } from "../../../hooks/useNavigation";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { getPatient } from "../../../redux/slice/patient/patientSlice";
import { WoundInfo } from "./AncillaryOrderForm";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomIconButton from "../../common/CustomIconButton";
import PDFViewer from "../../common/pdf/PDFViewer";
import CustomItemList from "../../common/CustomItemList";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

interface TissueTrackingState extends TissueTrackingInfo {
  checkBoxes: string[],
  selectAll?: boolean
}

export interface OrderItemRow {
  select: JSX.Element;
  orderItemId: number;
  serialNumber: string;
  size: string;
  tissueTrackingId: number
  productReturnId: number;
  productReturnStatus: string;
  units: number;
  woundLocation?: string;
  tissueTrackingDocumentId?: number;
  orderId?: number
  productId?: number
  tissueTrackingInfo?: any;
  productName?: string;
  appliedDate?: string;
  returnedDate?: string;
}


const TissueTracking = () => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setError,
    clearErrors,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<TissueTrackingState>();

  const { navigate } = useNavigation();
  const location = useLocation();
  const theme = useTheme();
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    datepickerWrapSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const dispatch = useAppDispatch();
  const [order, setOrder] = useState<OrderInfo>();
  const { orderItemsListInfo, addTissueTrackingStatus, orderDocumentInfo } = useOrderSlice();
  const [checkedList, setCheckedList] = useState<number[]>([]);
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });
  const [isAllAppliedOrReturned, setIsAllAppliedOrReturned] = useState(true);
  const [barcodeStickerInitialFiles, setBarcodeStickerInitialFiles] = useState<ExtendedFile[]>([]);
  const [imagesOfWoundsInitialFiles, setImagesOfWoundsInitialFiles] = useState<ExtendedFile[]>([]);
  const [medicalNotesInitialFiles, setMedicalNotesInitialFiles] = useState<ExtendedFile[]>([]);
  const [selectedRow, setSelectedRow] = useState<OrderItemRow[] | []>([]); 
  const [isEditing, setIsEditing] = useState(false); 
  const [mfrOrderId, setMFROrderId] = useState<{
    patientId: number | undefined;
    manufacturerOrderId: string | undefined;
  }>({
    patientId: 0,
    manufacturerOrderId: ""
  });
  const [showUpdateMFROrderIdPopup, setShowUpdateMFROrderIdPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });
  const [mfrOrderIDinOrderInfo, setMFROrderIDinOrderInfo] = useState<string>("");
  const [woundInfoRows, setWoundInfoRows] = useState<WoundInfo[]>([]);
  const [maxWoundRowIndex, setMaxWoundRowIndex] = useState(0); // CS - 746
  const { userInfo } = useAuthSlice();
    const [showPDF, setShowPDF] = useState<{
      isShow: boolean;
      productName: string;
      fileName: string;
    }>({ isShow: false, productName: "", fileName: "" });
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filters, setFilters] = useState({
    All: false,
    Available: true,
    Applied: false,
    ReturnSubmitted: false,
    ReturnPending: false,
    Returned: false
  });
  const [editableSerialNumber, setEditableSerialNumber] = useState<number | null>(null);
  const [serialNumberErrors, setSerialNumberErrors] = useState<number[]>([]);


// CS-746
  useEffect(() => {
    const fetchPatientData = async () => {
      const orderDetails: OrderInfo = location.state.selectedOrder;
      const patientId = orderDetails.patientId;
      setOrder(orderDetails);
      if (orderDetails) {
        setInitialValues(orderDetails);
        dispatch(getOrderItemsByOrderId(orderDetails.orderId));
        setValue("manufacturerOrderId", orderDetails.manufacturerOrderId || "");
        setIsEditing(orderDetails.manufacturerOrderId === "" || orderDetails.manufacturerOrderId === null || orderDetails.manufacturerOrderId === undefined ? true : false);
        setMFROrderIDinOrderInfo(orderDetails.manufacturerOrderId || "");
      }
      if (patientId !== undefined && patientId !== null) {
        const response = await dispatch(getPatient({ patientId: patientId.toString() }));
        // Retrieve all wound details and make options for the dropdown
        if (response.payload && response.payload.code === RESPONSE_CODES.SUCCESS_CODE) {
          const woundInfo = response.payload.data?.patientInfo?.find((info: any) => info.infoType === 'WOUND_INFO' && info.inactiveDate === null);
          let processedWoundData = woundInfo?.patientinfoData?.woundInfo?.woundData?.map((woundData: WoundInfo, i: number) => {
            const totalSize = parseFloat(woundData.totalSize as unknown as string);
            const totalSizeFormatted = isNaN(totalSize) ? '0.00' : totalSize.toFixed(2);
            const cptFormatted = Array.isArray(woundData.cpt) ? woundData.cpt.join(', ') : woundData.cpt;
            const woundInfoKey =  woundData.woundInfoKey ? woundData.woundInfoKey : generateUniqueId(maxWoundRowIndex > 0 ? maxWoundRowIndex + 1  : i + 1); // CS - 746
            return {
              woundType: woundData.woundType,
              cpt: cptFormatted,
              cptIds: woundData.cptIds,
              lCode: woundData.lCode,
              lCodeIds: woundData.lCodeIds,
              eCode: woundData.eCode,
              eCodeIds: woundData.eCodeIds,
              iCode: woundData.iCode,
              iCodeIds: woundData.iCodeIds,
              otherDxCodes: woundData.otherDxCodes,
              noOfApplications: woundData.noOfApplications,
              procedureLocation: woundData.procedureLocation,
              length: woundData.length,
              width: woundData.width,
              depth: woundData.depth,
              totalSize: totalSizeFormatted,
              displayTotalSize: `${totalSizeFormatted}cm² (${woundData.length} X ${woundData.width} X ${woundData.depth})`,
              woundInfoKey: woundInfoKey // CS - 746
            };
          });
          setWoundInfoRows(processedWoundData || []);
        }
      }
    };

    fetchPatientData();
  }, []);
  

// CS-746
     useEffect(() => {
       if ( woundInfoRows && woundInfoRows.length > 0) {
          const maxNumber = getMaxWoundInfoKey(woundInfoRows);
          setMaxWoundRowIndex(maxNumber);
      }
  }, [woundInfoRows]);

  const getMaxWoundInfoKey = (rows: WoundInfo[]): number => {
    const maxKey = rows.reduce((max, row) => {
      if (row.woundInfoKey) {
        const parts = row.woundInfoKey.split('_');
        const num = parseInt(parts[parts.length - 1], 10);
        if (!isNaN(num) && num > max) {
          return num;
        }
      }
      return max;
    }, 0);
    return maxKey;
  };

  const generateUniqueId = (index: number): string => {
    return `${`WOUND`}_${index}`;
  };

    const woundOptions = useMemo(() => {
      let options: { label: string; value: any }[] = [];
      woundInfoRows?.forEach((wound: any) => {
        const woundNumber = wound.woundInfoKey.split('_').pop() || '1';
        const dimensions = `(${wound.length}cm x ${wound.width}cm x ${wound.depth}cm)`;
        if (wound.procedureLocation?.trim()) {
          options.push({
            label: `${dimensions} - (${wound.procedureLocation})`,
            value: `${dimensions} - ${wound.procedureLocation}`
          });
        } else {
          options.push({
            label: `${dimensions}`,
            value: `${dimensions}`
          });
        }        
      });
      return options;
    }, [woundInfoRows]);

  const setInitialValues = (orderDetails: OrderInfo) => {
    if (orderDetails.patientId) {
      setValue('patientId', orderDetails.patientId);
    }else if (orderDetails.orderInfo?.PatientID){
      setValue('patientId', Number(orderDetails.orderInfo.PatientID));
    }
    // Restrict Date of Service as the default
    // if (dayjs(orderDetails.dateOfService).isValid()) {
    //   setValue("dateOfService", dayjs(orderDetails.dateOfService).format("MM/DD/YYYY"));
    // };
  };

  const handleDropZone = async (uploadedFiles: ExtendedFile[], fieldName: string) => {
    const attachmentBarcodeStickerArray: AttachmentBarcodeStickerInfo[] = [];
    if (uploadedFiles && uploadedFiles.length > 0) {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const base64File = String(await convertBase64(uploadedFiles[i]));
        const fileName = uploadedFiles[i].name;
        const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        const base64Substring = base64File.substring(
          fileExtension === "pdf" ? "data:application/pdf;base64,".length : "data:image/png;base64,".length,
          base64File.length
        );
        const attachmentBarcodeStickerObj: AttachmentBarcodeStickerInfo = {
          documentName: fileName,
          content: base64Substring,
          fileType: fileExtension
        };
        attachmentBarcodeStickerArray.push(attachmentBarcodeStickerObj);
      }
    }
    setValue("attachmentBarcodeSticker", attachmentBarcodeStickerArray);
    trigger("attachmentBarcodeSticker");

    // Clear all serial number errors
    if (attachmentBarcodeStickerArray.length > 0) {
      setSerialNumberErrors([]);
    }
  };

  const handleImageOfWounds = async (uploadedFiles: ExtendedFile[], fieldName: string) => {
    const attachmentImageOfWoundsArray: AttachmentImageOfWoundsInfo[] = [];
    if (uploadedFiles && uploadedFiles.length > 0) {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const base64File = String(await convertBase64(uploadedFiles[i]));
        const fileName = uploadedFiles[i].name;
        const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        const base64Substring = base64File.substring(
          fileExtension === "pdf" ? "data:application/pdf;base64,".length : "data:image/png;base64,".length,
          base64File.length
        );
        const attachmentImageOfWoundsObj: AttachmentImageOfWoundsInfo = {
          documentName: fileName,
          content: base64Substring,
          fileType: fileExtension
        };
        attachmentImageOfWoundsArray.push(attachmentImageOfWoundsObj);
      }
    }
    setValue("attachmentImageOfWounds", attachmentImageOfWoundsArray);
    trigger("attachmentImageOfWounds");
  };

  const handleMedicalNotes = async (uploadedFiles: ExtendedFile[], fieldName: string) => {
    const attachmentMedicalNotesArray: AttachmentMedicalNotesInfo[] = [];
    if (uploadedFiles && uploadedFiles.length > 0) {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const base64File = String(await convertBase64(uploadedFiles[i]));
        const fileName = uploadedFiles[i].name;
        const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        const base64Substring = base64File.substring(
          fileExtension === "pdf" ? "data:application/pdf;base64,".length : "data:image/png;base64,".length,
          base64File.length
        );
        const attachmentMedicalNotesObj: AttachmentMedicalNotesInfo = {
          documentName: fileName,
          content: base64Substring,
          fileType: fileExtension
        };
        attachmentMedicalNotesArray.push(attachmentMedicalNotesObj);
      }
    }
    setValue("attachmentMedicalNotes", attachmentMedicalNotesArray);
    trigger("attachmentMedicalNotes");
  };


  const handleDateOfService = (date: Date | null) => {
    if (dayjs(date).isValid()) {
      setValue("dateOfService", dayjs(date).format("MM/DD/YYYY"));
    };
  };

  const onSubmit = () => {
    if (!isAllAppliedOrReturned) {
      if (checkedList?.length > 0) {
        clearErrors("attachmentBarcodeSticker");
        clearErrors("dateOfService");
        clearErrors("checkBoxes");
        clearErrors("selectAll");
        setShowOptionsPopup({
          message: "Do you really want to submit the Record TX? ",
          show: true,
          variant: "confirm",
          buttons: [{ name: "Confirm", color: 'primary', onClick: () => submitTissueTracking() }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
        });
      }
    } else {
      let warningObj: SingleAlertInfo = {
        message: "Order items aren't available to Tissue Track.",
        alertType: "warning"
      };
      dispatch(setSingleAlertObj(warningObj));
    }
  };

  const submitTissueTracking = async () => {
    const { woundLocations, serialNumbers, checkBoxes, selectAll, ...submitObject } = getValues();
    const orderItemList: OrderItemInfo[] = [];
  
    checkedList?.map(itemId => {
      const selectedItem = selectedRow.find(row => row.orderItemId === itemId);
      const woundLocationValue = ((woundLocations ?? {}) as { [key: string]: string })[`woundLocation${itemId}`];
      const selectedOption = woundOptions.find(option => option.value === woundLocationValue);
      const woundLocationLabel = selectedOption?.label || "";
  
      orderItemList.push({
        orderItemId: itemId,
        //@ts-ignore
        serialNumber: serialNumbers[`serialNumber${itemId}`],
        //@ts-ignore
        woundLocationValue: woundLocationValue,
        //@ts-ignore
        woundLocationLabel: woundLocationLabel,
        orderId: selectedItem?.orderId,
        productId: selectedItem?.productId
      });
    });
  
    if (orderItemList?.length > 0) {
      submitObject.orderItems = orderItemList;
    };
    const data = {
      ...submitObject,
      isBarcodeStickerSkipped: getValues("isBarcodeStickerSkipped"),
      manufacturerOrderId: getValues("manufacturerOrderId")
    }
    const response = await dispatch(
      addTissueTracking({tissueTrackingData: data,  interceptorOption: {skipErrorPopup: true}})
    );
    if(response.payload && response.payload.code === RESPONSE_CODES.FAILED_CODE) {
      let alertMessage = response.payload?.message;
      const alertObj: SingleAlertInfo = {
        message: alertMessage,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
    cancelOptionPopup();
  };

  const onError = (_errors: any, e: any) => {
    const { attachmentBarcodeSticker, dateOfService } = getValues();
    let hasError = false;
    
    if (checkedList?.length <= 0) {
      let errorObj: SingleAlertInfo = {
        message: "Please select at least one Order item.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      hasError = true;
      cancelOptionPopup();
      // clearErrors("attachmentBarcodeSticker");
    } else if (dateOfService === undefined || dateOfService === "" || dateOfService === null) {
      let errorObj: SingleAlertInfo = {
        message: "Date of Service is Required.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      hasError = true;
      cancelOptionPopup();
      clearErrors("checkBoxes");
      clearErrors("selectAll");

    } else if ((selectedRow.length === 0 || hasSerialNumberPresent(selectedRow)) && attachmentBarcodeSticker?.length <= 0) {
      // Find which items are missing serial numbers
      const serialNumbers = getValues("serialNumbers");
      const itemsWithMissingSerialNumbers: number[] = [];

      checkedList?.forEach(itemId => {
        //@ts-ignore
        if (!serialNumbers?.[`serialNumber${itemId}`]) {
          itemsWithMissingSerialNumbers.push(itemId);
        }
      });

      setSerialNumberErrors(itemsWithMissingSerialNumbers);
      let errorObj: SingleAlertInfo = {
        message: "Either a serial number or a barcode sticker is required.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      hasError = true;
      cancelOptionPopup();
      clearErrors("checkBoxes");
      clearErrors("selectAll");

      // } else if (attachmentImageOfWounds?.length <= 0) {
      //   let errorObj: SingleAlertInfo = {
      //     message: "Image of wounds are required.",
      //     alertType: "error"
      //   };
      //   dispatch(setSingleAlertObj(errorObj));
      //   hasError = true;
      //   cancelOptionPopup();
      //   cancelOptionPopup();
      //   clearErrors("checkBoxes");
      //   clearErrors("selectAll");
      //   clearErrors("attachmentBarcodeSticker");
    }
    else {
      setSerialNumberErrors([]);
      const serialNumbers = getValues("serialNumbers");
      const woundLocations : any = getValues("woundLocations");
      let isWoundLocationValid = true;
      let isNotEmpty = false;
      const hasBarcodeSticker = getValues("attachmentBarcodeSticker").length > 0;

      checkedList?.map(itemId => {
        //@ts-ignore
        if (serialNumbers[`serialNumber${itemId}`]) {
          isNotEmpty = true;
        }
        //@ts-ignore
        else if (!serialNumbers[`serialNumber${itemId}`] && hasBarcodeSticker) {
          isNotEmpty = true;
        }
        if (!woundLocations?.[`woundLocation${itemId}`]) {
          isWoundLocationValid = false;
        }
      });

      if (isNotEmpty) {
        clearErrors("attachmentBarcodeSticker");
        clearErrors("checkBoxes");
        clearErrors("selectAll");
      }
  
      if (!isWoundLocationValid) {
        let errorObj: SingleAlertInfo = {
          message: "Please select a Wound Location for all selected Order items.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errorObj));
        hasError = true;
        cancelOptionPopup();
        clearErrors("checkBoxes");
        clearErrors("selectAll");
      }
    }
    if (Object.keys(errors).length > 0 || hasError) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      onSubmit();
    }
  };

  useEffect(() => {
    if (addTissueTrackingStatus === 'success') {
      let successObj: SingleAlertInfo = {
        message: `Record Product Usage for Order ${order?.orderNumber} has been submitted successfully.`,
        alertType: "success"
      };
      dispatch(setSingleAlertObj(successObj));
      dispatch(updateElementInOrderSlice(
        {
          elementName: "addTissueTrackingStatus",
          value: "idle"
        }
      ));
      cancelOptionPopup();
      navigate(-1);
    }
  }, [addTissueTrackingStatus])

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  useEffect(() => {
    if (orderItemsListInfo) {
      const isAllItemAppliedOrReturned = orderItemsListInfo.every((item: { productReturnId: any; tissueTrackingId: any; }) => item.productReturnId || item.tissueTrackingId);
      setIsAllAppliedOrReturned(!!isAllItemAppliedOrReturned);
    };
  }, [orderItemsListInfo]);

  const orderItemRow: OrderItemRow[] = useMemo(() => {
    let orderItems: OrderItemRow[] = [];
    if (orderItemsListInfo) {
      orderItems = orderItemsListInfo.map(item => {
        let woundDetails: any = {}; 
        if (item.tissueTrackingId) {
          const tissueTrackingInfo: Record<string, any> = item.tissueTrackingInfo || {}; 
          const woundKey = `woundData_${item.orderItemId}`;
          woundDetails = tissueTrackingInfo[woundKey] || {};  
        }
  
        //@ts-ignore
        setValue(`serialNumbers.serialNumber${item.orderItemId}`, item.serialNumber);
        return {
          select: <CustomCheckbox control={control} name="select" />,
          orderItemId: item.orderItemId,
          serialNumber: item.serialNumber,
          size: item.size,
          tissueTrackingId: item.tissueTrackingId,
          productReturnId: item.productReturnId,
          productReturnStatus: item.productReturnStatus,
          units: item.units,
          woundLocation: woundDetails?.woundLocationLabel,
          tissueTrackingDocumentId: item.tissueTrackingDocumentId,
          orderId: item.orderId,
          productId: item.productId,
          tissueTrackingInfo: item.tissueTrackingInfo ? {
            imagesOfWounds: item.tissueTrackingInfo.imagesOfWounds || [],
            medicalNotes: item.tissueTrackingInfo.medicalNotes || [],
            barcodes: item.tissueTrackingInfo.barcodes || [],
          } : null,
          productName: order?.productName,
          appliedDate: item.appliedDate,
          returnedDate: item.returnedDate
        }
      })
      .sort((a, b) => a.units - b.units);;
    }
    return orderItems;
  }, [orderItemsListInfo]);

  const handleCheckBox = (e: React.ChangeEvent<HTMLInputElement>, row: OrderItemRow) => {
    const { checked } = e.target;
    if (checked) {
      row && setCheckedList(prevValues => {
        const newValues: number[] = [...prevValues];
        newValues.push(row.orderItemId);
        return newValues;
      });

      setSelectedRow((data)=>{
        let selectedOrderItemRow: OrderItemRow[]  = [];

        data?.map((itemRow)=>selectedOrderItemRow.push(itemRow));
        selectedOrderItemRow.push(row);
        return selectedOrderItemRow;
      })
    } else {
      setCheckedList(checkedList.filter(item => item !== row.orderItemId));

      setSelectedRow((data)=>{
        return data?.filter((itemRow)=> itemRow.orderItemId !== row.orderItemId) || [];
      })
      setValue("selectAll", false);

      // Clear error for this specific item when unchecked
      setSerialNumberErrors(prev => prev.filter(id => id !== row.orderItemId));
    }
  };

// CS-746
  const handleLocationChange = (row: OrderItemRow, value: any) => {
    if (value) {
      const selectedOption = woundOptions.find(option => option.value === value);
      const label = selectedOption?.label || "";
  
      let existingRow = selectedRow.find(item => item.orderItemId === row.orderItemId);
  
      if (existingRow) {
        let updatedRow: OrderItemRow = {
          ...existingRow,
          woundLocation: value,
        };
  
        setSelectedRow((prevRows) => {
          let otherRows = prevRows?.filter((item) => item.orderItemId !== row.orderItemId) || [];
          return [...otherRows, updatedRow];
        });
      }
    }
  };
//
  const checkSerialNumberPresent = (row: OrderItemRow) => {
    if (checkedList.includes(row.orderItemId)) {
      return false;
    }
    return true;
  };

  const handleSerialNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, row: OrderItemRow) => {
    let existingRow = selectedRow.find(item => item.orderItemId === row.orderItemId);
    
    if (existingRow && existingRow !== null) {
      let updatedRow: OrderItemRow = {
        ...existingRow,
        serialNumber: e.target.value,
      };
  
      setSelectedRow((prevRows) => {
        let otherRows = prevRows?.filter((item) => item.orderItemId !== row.orderItemId) || [];
        return [...otherRows, updatedRow];
      });
    }
    // Trigger validation on change
    trigger(`serialNumbers.serialNumber${row.orderItemId}` as any);

    // Clear error for this specific item when user enters a value
    if (e.target.value && e.target.value.trim() !== "") {
      setSerialNumberErrors(prev => prev.filter(id => id !== row.orderItemId));
  }
  }

  const hasSerialNumberPresent = (row: OrderItemRow[]) => {
    let hasNoSerialNumber = false;

    row?.map((element: OrderItemRow) => {
      if (element.serialNumber === "" || element.serialNumber === null) {
        hasNoSerialNumber = true;
      }
    })

    return hasNoSerialNumber;
  };

  const isCheckBoxDisable = (row: OrderItemRow) => {
    if (row?.tissueTrackingId) {
      return true;
    }
    if (row?.productReturnId && row?.productReturnStatus !== ORDER_AVAILABILITY.RETURN_REJECTED) {
      return true;
    }
    return false;
  };

  // CS-1164 START
  const orderItemStatus = (row: OrderItemRow) => {
    if (row?.tissueTrackingId) {
      return "Applied";
    } else if (row?.productReturnId) {
      // return `Returned`;
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_INITIATED) {
        return "Return Initiated";
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING) {
        return "Return Pending";
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURNED) {
        return "Returned";
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) {
        return "Available(Return Rejected)";
      }
    }
    return "Available";
  };
  // CS-1164 END

  const handleSelectAllCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (checked) {
      const selectList: number[] = [];
      orderItemRow?.forEach(row => {
        if (!isCheckBoxDisable(row)) {
          selectList.push(row.orderItemId);
          //@ts-ignore
          setValue(`checkBoxes.checkbox${row.orderItemId}`, true);
  
          setSelectedRow((prevSelectedRows) => {
            if (!prevSelectedRows?.some(itemRow => itemRow.orderItemId === row.orderItemId)) {
              return [...prevSelectedRows, row];
            }
            return prevSelectedRows;
          });
        }
      });
  
      setCheckedList(selectList);

    } else {
      setCheckedList([]);
      orderItemRow?.forEach(row => {
        if (!isCheckBoxDisable(row)) {
          //@ts-ignore
          setValue(`checkBoxes.checkbox${row.orderItemId}`, false);

          setSelectedRow((prevSelectedRows) => {
            return prevSelectedRows?.filter(itemRow => itemRow.orderItemId !== row.orderItemId) || [];
          });
        }
      });
    }
  };

  const renderMFROrderIDTextfieldComponent = () => {
    if (isEditing) {
      return (
        <CustomTextField
          controllerName="manufacturerOrderId"
          autoFocus
          control={control}
          defaultValue={mfrOrderId.manufacturerOrderId}
          onChange={(event) => {
            const inputData = event.target.value;
            setMFROrderId(prev => ({
              ...prev,
              manufacturerOrderId: inputData
            }));
            setValue("manufacturerOrderId", inputData);
          }}
          onBlur={() => {
            setIsEditing(false);
            const newValue = getValues("manufacturerOrderId");
            const originalValue = mfrOrderIDinOrderInfo;

            if (newValue !== originalValue) {
              setShowUpdateMFROrderIdPopup({
                show: true,
                message: `Do you want to update the current Manufacturer Order ID ${originalValue ? `from ${originalValue}` : ''} to ${newValue}?`,
                variant: "confirm",
                title: "Update Manufacturer Order ID",
                buttons: [
                  {
                    name: "Yes",
                    color: "success",
                    onClick: () => {
                      // Update both form and local state
                      setValue("manufacturerOrderId", newValue);
                      setMFROrderId(prev => ({
                        ...prev,
                        manufacturerOrderId: newValue
                      }));
                      setOrder(prev => prev ? {
                        ...prev,
                        manufacturerOrderId: newValue
                      } : prev);
                      setMFROrderIDinOrderInfo(newValue);
                      setShowUpdateMFROrderIdPopup(prev => ({ ...prev, show: false }));
                    }
                  },
                  {
                    name: "No",
                    color: "error",
                    onClick: () => {
                      // Revert changes
                      setValue("manufacturerOrderId", originalValue);
                      setMFROrderId(prev => ({
                        ...prev,
                        manufacturerOrderId: originalValue
                      }));
                      setShowUpdateMFROrderIdPopup(prev => ({ ...prev, show: false }));
                    }
                  }
                ]
              });
            }
          }}
          name="manufacturerOrderId"
        />
      );
    }
  }

  const handlePreviewAction = async (row: OrderItemRow) => {
    let documentId = row.tissueTrackingDocumentId
      ? row.tissueTrackingDocumentId
      : 0;
    const actionResult = await dispatch(fetchOrderDocumentById(documentId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName:
          order?.productName +
          (orderItemStatus(row) === `Returned`
            ? ` - RETURN`
            : ` - TISSUE TRACKING`),
        fileName:
          orderItemStatus(row) === `Returned`
            ? `RETURN_${order?.orderNumber}`
            : `TISSUE_TRACKING_${order?.orderNumber}`,
      });
    }
  };

  const handleImageViewAction = async (row: OrderItemRow) => {
    let documentIds = row.tissueTrackingInfo?.imagesOfWounds ?? [];
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row?.productName + ` - TISSUE TRACKING`,
        fileName: `TISSUE_TRACKING_${order?.orderNumber}`,
      });
    }
  };

  const handleViewMedicalNotesAction = async (row: OrderItemRow) => {
    let documentIds = row.tissueTrackingInfo?.medicalNotes ?? [];
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: row?.productName + " - TISSUE TRACKING",
        fileName: `TISSUE_TRACKING_MEDICAL_NOTES_${order?.orderNumber}`,
      });
    }
  };
  
  const columns = [
    {
      id: "select",
      label: isMobile ? (
        <Box />
      ) : (
         filters.Available ? (
        <CustomCheckbox
          control={control}
          controllerName="selectAll"
          onChange={handleSelectAllCheckbox}
          tooltipMessage="Select All"
          tooltipPlacement="top"
          rules={!isAllAppliedOrReturned ? { required: true } : {}}
        />) : (
            <Box />
        )
      ),
      minWidth: 50,
    },
    { id: "size", label: "Size", minWidth: 100 },
    { id: "serialNumber", label: "Serial Number"},
    { id: "status", label: "Status", minWidth: 110 },
    { id: "woundLocation", label: "Wound Location" },
    { id: "document", label: "Document"},
    { id: "appRet", label: "App/Ret", minWidth: 100},
  ];

  const filteredRows = orderItemRow.filter((row) => {
    const status = orderItemStatus(row);
    if (filters.All) return true;
    return (
      (filters.Available && (status === "Available" || status === "Available(Return Rejected)")) ||
      (filters.Applied && status === "Applied") ||
      (filters.ReturnSubmitted && status === "Return Initiated") ||
      (filters.ReturnPending && status === "Return Pending") ||
      (filters.Returned && status === "Returned")
    );
  });

  const handleSerialNumberClick = (row: OrderItemRow) => {
    if (checkedList.includes(row.orderItemId)) {
      setEditableSerialNumber(row.orderItemId);
    }
  };

  const handleSerialNumberBlur = async (row: OrderItemRow) => {
    // Trigger validation on blur
    const isValid = await trigger(`serialNumbers.serialNumber${row.orderItemId}` as any);
    
    // Only close the text field if validation passes
    if (isValid) {
      setEditableSerialNumber(null);
    }
  };
      
  const rows = filteredRows?.map((row) => ({
    select: (
      <CustomCheckbox
        isDisabled={isCheckBoxDisable(row)}
        onChange={(e) => handleCheckBox(e, row)}
        control={control}
        controllerName={`checkBoxes.checkbox${row.orderItemId}`}
        rules={!isAllAppliedOrReturned ? { required: true } : {}}
      />
    ),
    size: row.size,
    serialNumber: (
      checkedList.includes(row.orderItemId) ? (
        editableSerialNumber === row.orderItemId ? (
          <CustomTextField
            control={control}
            isDisabled={false}
            controllerName={`serialNumbers.serialNumber${row.orderItemId}`}
            label="Serial Number"
            sx={{ width: "100%" }}
            onBlur={() => handleSerialNumberBlur(row)}
            onChange={(e) => handleSerialNumberChange(e, row)}
            rules={{ maxLength: { value: 50, message: "Serial Number cannot exceed 50 characters" } }}
            autoFocus
          />
        ) : (
<Typography
  onClick={() => handleSerialNumberClick(row)}
  sx={{
    cursor: "pointer",
    color: serialNumberErrors.includes(row.orderItemId) ? "#e84236" : "white",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }}
>
  {/* @ts-ignore */}
  {getValues(`serialNumbers.serialNumber${row.orderItemId}`) || "Click to Edit"}
</Typography>
        )
      ) : (
        <Typography
          sx={{
              cursor: "not-allowed",
              color: "#B0B0B0",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
          }}
        >
          {row.serialNumber || "Not Available"}
        </Typography>
      )
    ),
    status: orderItemStatus(row),
    woundLocation: !isCheckBoxDisable(row) && checkedList.includes(row.orderItemId) ? (

      <CustomDropdownField
        label="Select Wound"
        name={`woundLocations.woundLocation${row.orderItemId}`}
        options={woundOptions}
        control={control}
        onChangeSelect={(e, v) => handleLocationChange(row, v)}
        rules={{ required: "Select Wound is required." }}
      />
    ) : (
      <Typography>{row.woundLocation}</Typography>
    ),
    document: (
      <Grid container spacing={1} alignItems="center">
        {row.tissueTrackingDocumentId && (
          <CustomIconButton
            tooltipPlacement="top"
            tooltipMessage="Document"
            color="info"
            size="small"
            variant="outlined"
            onClick={() => handlePreviewAction(row)}
            sx={{ mr: 1 }}
          >
            <AssignmentOutlined fontSize="small" />
          </CustomIconButton>
        )}
        {row.tissueTrackingInfo?.imagesOfWounds?.length > 0 && (
          <CustomIconButton
            tooltipPlacement="top"
            tooltipMessage="Image of Wound"
            color="info"
            size="small"
            variant="outlined"
            onClick={() => handleImageViewAction(row)}
            sx={{ mr: 1 }}
          >
            <WallpaperOutlined fontSize="small" />
          </CustomIconButton>
        )}
        {row.tissueTrackingInfo?.medicalNotes?.length > 0 && (
          <CustomIconButton
            tooltipPlacement="top"
            tooltipMessage="Medical Notes"
            color="info"
            size="small"
            variant="outlined"
            onClick={() => handleViewMedicalNotesAction(row)}
          >
            <NoteAddOutlined fontSize="small" />
          </CustomIconButton>
        )}
      </Grid>
    ),
    appRet: (() => {
        const applied = row?.appliedDate;
        const returned = row?.returnedDate;
        if (row?.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) return "";

        if (applied) return applied;
        if (returned) return returned;
        return "";
    })(),
  }));
  
  const isBarcodeRequired = useMemo(
    () => selectedRow.length > 0 && hasSerialNumberPresent(selectedRow),
    [selectedRow]
  );

  const FieldLabel = ({ label, required = false, helpMessage }: { label: string; required?: boolean; helpMessage?: string }) => (
    <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary, mb: 1, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}
      {required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
      {helpMessage && <CustomHelpMessage title="Help" message={helpMessage} />}
    </Typography>
  );

  const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box display="flex" alignItems="center" py={0.9} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, flex: 1 }} component="div">
        {value ?? "—"}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        mx: { xs: "10px", sm: "20px" },
        mt: "12px",
        pb: "72px",
      }}
    >
      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, productName: "", fileName: "" })
          }
          title={showPDF.productName}
          fileName={showPDF.fileName}
          base64String={orderDocumentInfo?.content || ""}
        />
      )}

      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}
      {showUpdateMFROrderIdPopup.show && (
        <OptionsPopup
          open={showUpdateMFROrderIdPopup.show}
          onClose={() => setShowUpdateMFROrderIdPopup({ message: "", show: false })}
          variant={showUpdateMFROrderIdPopup.variant}
          message={showUpdateMFROrderIdPopup.message}
          buttons={showUpdateMFROrderIdPopup.buttons}
          title={showUpdateMFROrderIdPopup.title}
          obj={showUpdateMFROrderIdPopup.obj}
        />
      )}

      <Box mb={1.5} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} />
        <Box data-testid="header">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Orders / Record Product Usage
          </Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            Record Product Usage
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            Document the procedure and the products used, applied, or implanted.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ order: { xs: 2, md: 1 } }}>
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <Box sx={cardSx}>
              <Box sx={{ px: 2.8, pt: 2, pb: 2.8 }}>
                {filters.Available && (
                  <Box sx={{ mb: 2.5, maxWidth: { md: 280 } }}>
                    <FieldLabel
                      label="Date of Service (DOS)"
                      required={!isAllAppliedOrReturned}
                      helpMessage="Select the date the product was applied to the patient."
                    />
                    <Box sx={datepickerWrapSx(!!errors.dateOfService)}>
                      <CustomDatePicker
                        control={control}
                        onChange={handleDateOfService}
                        controllerName="dateOfService"
                        label="Date Of Service"
                        rules={
                          !isAllAppliedOrReturned
                            ? { required: "Date of Service is Required." }
                            : {}
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                )}

                <FieldLabel
                  label="Select Applied Products"
                  helpMessage="Check the box of each product item that was utilized or applied to this patient on the above Date of Service. Ensure the Serial# aligns with the Tissue ID# on the barcode sticker found in the product's original packaging."
                />
                <Box
                  sx={{
                    borderRadius: "12px",
                    border: `1px solid ${mTokens.border}`,
                    backgroundColor: mTokens.bg,
                    p: 1.5,
                  }}
                >
                  <Box
                    display="flex"
                    gap={2}
                    alignItems="center"
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: "10px",
                      border: `1px solid ${mTokens.border}`,
                      backgroundColor: mTokens.headerFooterBg,
                      mb: 1.5,
                      flexWrap: "wrap",
                      rowGap: 1,
                      [theme.breakpoints.down("sm")]: {
                        gap: 1,
                        justifyContent: "flex-start",
                      },
                    }}
                  >
                    {["Available", "Applied", "ReturnSubmitted", "ReturnPending", "Returned"].map((label) => (
                      <CustomCheckbox
                        key={label}
                        controllerName={`filterCheck.${label}`}
                        control={control}
                        label={label === "ReturnSubmitted" ? "Return Initiated" : label === "ReturnPending" ? "Return Pending" : label}
                        checked={filters[label as keyof typeof filters]}
                        onChange={(e) => {
                          const { checked } = e.target;
                          setFilters((prev) => ({
                            ...prev,
                            [label]: checked
                          }));
                        }}
                      />
                    ))}
                  </Box>
                  <CustomItemList columns={columns} rows={rows} maxHeight={400} />
                </Box>
              </Box>
            </Box>

            {filters.Available && (
              <Box sx={{ ...cardSx, mt: 1.5 }}>
                <Box sx={{ px: 2.8, pt: 2, pb: 2.8 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <FieldLabel
                        label={`Upload Image of Barcode Sticker(s)${isBarcodeRequired ? "" : " [Optional]"}`}
                        required={isBarcodeRequired}
                        helpMessage="Take a photo or upload an image of the barcode sticker(s)."
                      />
                      <Dropzone
                        onFileChangeTest={handleDropZone}
                        name="attachmentBarcodeSticker"
                        onFileChange={() => {}}
                        control={control}
                        subLabel={{
                          textColor: mTokens.error,
                          text:
                            (selectedRow.length === 0 ||
                              hasSerialNumberPresent(selectedRow)) &&
                            getValues("attachmentBarcodeSticker")?.length <=
                              0 ? (
                              <></>
                            ) : (
                              <span
                                style={{
                                  fontStyle: "italic",
                                  fontSize: "14px",
                                }}
                              >
                                (Not Required)
                              </span>
                            ),
                        }}
                        rules={
                          selectedRow.length === 0 ||
                          !hasSerialNumberPresent(selectedRow)
                            ? undefined
                            : {
                                required:
                                  "Barcode Sticker Attachment is required.",
                              }
                        }
                        initialFiles={barcodeStickerInitialFiles}
                        holdOnChange={true}
                        isDisabled={getValues("isBarcodeStickerSkipped")}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FieldLabel
                        label="Upload Image of Wound [Optional]"
                        helpMessage="Upload images of wound. This will bolster your protection during any audit."
                      />
                      <Dropzone
                        onFileChangeTest={handleImageOfWounds}
                        name="attachmentImageOfWounds"
                        onFileChange={() => {}}
                        control={control}
                        // rules={{ required: "Image of wounds are required." }}
                        initialFiles={imagesOfWoundsInitialFiles}
                        holdOnChange={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FieldLabel
                        label="Upload Medical Notes [Optional]"
                        helpMessage="Upload Medical Notes. This will bolster your protection during any audit."
                      />
                      <Dropzone
                        onFileChangeTest={handleMedicalNotes}
                        name="attachmentMedicalNotes"
                        onFileChange={() => {}}
                        control={control}
                        initialFiles={medicalNotesInitialFiles}
                        holdOnChange={true}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2.5 }}>
                    <FieldLabel label="Any Additional Comments for this Tissue Utilization Record? List them here." />
                    <Box sx={inputFieldSx()}>
                      <CustomTextField
                        controllerName="comment"
                        control={control}
                        label=""
                        multiline
                        rows={4}
                        maxRows={4}
                      />
                    </Box>
                  </Box>

                  <Box
                    display="flex"
                    justifyContent={{ xs: "stretch", sm: "flex-end" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={1.2}
                    mt={2.5}
                  >
                    <Button
                      type="submit"
                      startIcon={<ClassOutlined />}
                      variant="contained"
                      removeUpperCase
                      sx={{
                        ...primaryBtnSx,
                        minWidth: { sm: 140 },
                        backgroundColor: mTokens.accent,
                        boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                        "& .MuiTypography-root": { color: "#ffffff !important" },
                        "& .MuiButton-startIcon": { color: "#ffffff" },
                      }}
                    >
                      Submit
                    </Button>
                    <Button
                      color="error"
                      variant="outlined"
                      removeUpperCase
                      startIcon={<Cancel />}
                      onClick={() => navigate(-1)}
                      sx={{
                        ...cancelBtnSx,
                        minWidth: { sm: 140 },
                        "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                        "& .MuiButton-startIcon": { color: mTokens.error },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </form>
        </Box>

        <Box sx={{ ...cardSx, order: { xs: 1, md: 2 } }}>
          <SectionHeader icon={<PersonOutlined fontSize="small" />} title="Info" />
          <Box sx={{ px: 2.8, py: 1.5 }}>
            <InfoRow
              label="Patient"
              value={order?.patientName || order?.orderInfo?.patient || "-"}
            />
            <InfoRow
              label="Product"
              value={`${order?.productName} (${order?.manufacturerName})`}
            />
            <InfoRow label="Order ID" value={order?.orderNumber || "-"} />
            <Box display="flex" alignItems="center" py={0.9}>
              {!order?.manufacturerOrderId && userInfo.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE ? "" : (
                <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>
                  Manufacturer&nbsp;Order&nbsp;ID
                </Typography>
              )}

              {order?.manufacturerOrderId ? (
                <Box sx={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                  {isEditing && userInfo.entityName !== USER_ROLE_ENTITY_NAMES.PRACTICE ? (
                    <Box sx={inputFieldSx()}>{renderMFROrderIDTextfieldComponent()}</Box>
                  ) : (
                    <Tooltip title="Click to edit" placement="top" arrow>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          component="span"
                          onClick={() => setIsEditing(true)}
                          sx={{
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            color: mTokens.textPrimary,
                            "&:hover": { textDecoration: "underline", color: mTokens.accent },
                          }}
                        >
                          {getValues("manufacturerOrderId") || "-"}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ) : userInfo.entityName !== USER_ROLE_ENTITY_NAMES.PRACTICE && (
                <Box sx={{ ...inputFieldSx(), flex: 1, minWidth: 0 }}>
                  <CustomTextField
                    controllerName="manufacturerOrderId"
                    control={control}
                    label=""
                    autoFocus={isEditing}
                    onBlur={() => setIsEditing(false)}
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TissueTracking;
